import {
    HttpTransportType,
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
} from '@microsoft/signalr';
import { SignalREventName, SignalREventsMap } from './types';
import { SignalRMethods } from './types/signalR.methods';

export class SignalRCore {
    private connection: HubConnection;
    private groups: Set<string> = new Set();
    private reconnectedHandlers = new Set<() => void>();

    constructor(url: string) {
        this.connection = new HubConnectionBuilder()
            .withUrl(url, {
                accessTokenFactory: () => url,
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect()
            .build();

        this.connection.onclose((error) => {
            if (error) {
                console.error('SignalR connection closed with error:', error);
            } else {
                console.log('SignalR connection closed');
            }
        });

        this.connection.onreconnected(async (id) => {
            console.log(`SignalR reconnected with id: ${id}`);
            await this.rejoinGroups();
            this.reconnectedHandlers.forEach((handler) => handler());
        });
    }

    public async start(): Promise<void> {
        if (this.connection && this.connection.state !== HubConnectionState.Disconnected) {
            return;
        }

        try {
            await this.connection?.start();
            console.log('SignalR connected');
        } catch (error) {
            console.error('SignalR start failed:', error);
            setTimeout(() => {
                this.start();
            }, 5000);
        }
    }

    public stop() {
        return this.connection?.stop();
    }

    public get isConnected(): boolean {
        return !!this.connection && this.connection.state === HubConnectionState.Connected;
    }

    public subscribe<E extends SignalREventName>(
        event: E,
        handler: (data: SignalREventsMap[E]) => void
    ) {
        this.connection?.on(event, handler);
        return () => this.unsubscribe(event, handler);
    }

    public unsubscribe<E extends SignalREventName>(
        event: E,
        handler: (data: SignalREventsMap[E]) => void
    ) {
        return this.connection?.off(event, handler);
    }

    public subscribeReconnected(handler: () => void): () => void {
        this.reconnectedHandlers.add(handler);
        return () => {
            this.reconnectedHandlers.delete(handler);
        };
    }

    public async joinGroup(name: string): Promise<void> {
        if (!name) return;
        if (!this.isConnected) {
            console.warn(
                `Cannot join group "${name}" because connection is not active or name is empty.`
            );
            return;
        }
        if (this.groups.has(name)) {
            console.log(`Already joined group ${name}`);
            return;
        }
        try {
            await this.connection.invoke(SignalRMethods.JoinGroup, name);
            this.groups.add(name);
            console.log(`Joined group ${name}`);
        } catch (err) {
            console.error(`Failed to join group ${name}:`, err);
        }
    }

    public async leaveGroup(name: string): Promise<void> {
        if (!this.isConnected) {
            console.warn(`Cannot leave group "${name}" because connection is not active.`);
            return;
        }
        try {
            await this.connection.invoke(SignalRMethods.LeaveGroup, name);
            this.groups.delete(name);
            if (!this.groups.has(name)) {
            }
            console.log(`Left group ${name}`);
        } catch (err) {
            console.error(`Failed to leave group ${name}:`, err);
        }
    }

    private async rejoinGroups(): Promise<void> {
        if (!this.isConnected) return;

        for (const group of Array.from(this.groups)) {
            try {
                await this.connection.invoke(SignalRMethods.JoinGroup, group);
                console.log(`Rejoined group ${group}`);
            } catch (err) {
                console.error(`Failed to rejoin group ${group}:`, err);
            }
        }
    }
}

export const signalRCore = new SignalRCore(process.env.REACT_APP_ORDERS_HUB!);
